import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { cn } from "./cn";

/**
 * Form primitives (shadcn/ui style).
 *
 * `forwardRef` because a form library — or a plain `ref` — needs the DOM node,
 * and a component that swallows it cannot be focused or measured.
 */

const FIELD_BASE =
  "flex w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:cursor-not-allowed disabled:opacity-50";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Marks the field invalid for assistive technology, not just visually. */
  readonly invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(FIELD_BASE, "h-9", invalid && "border-red-500 focus-visible:ring-red-400", className)}
      aria-invalid={invalid}
      {...props}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(FIELD_BASE, "min-h-[80px]", invalid && "border-red-500", className)}
      aria-invalid={invalid}
      {...props}
    />
  );
});

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  /** Renders a marker and is announced as required by the input it labels. */
  readonly required?: boolean;
}

export function Label({ className, required = false, children, ...props }: LabelProps) {
  return (
    <label className={cn("text-sm font-medium text-neutral-700", className)} {...props}>
      {children}
      {required ? (
        <span className="ml-0.5 text-red-600" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  );
}

/** A form row: label, control, and the error message for that control. */
export interface FieldProps {
  readonly label: string;
  readonly htmlFor?: string;
  readonly required?: boolean;
  /** Shown under the control and linked via `aria-describedby`. */
  readonly error?: string;
  readonly hint?: string;
  readonly children: ReactNode;
}

export function Field({ label, htmlFor, required, error, hint, children }: FieldProps) {
  // The label is associated with its control programmatically, not just
  // visually (AGENTS.md §37): callers who do not pass `htmlFor` get an id
  // injected into the single control they wrap.
  const generatedId = useId();
  const controlId = htmlFor ?? generatedId;

  const control =
    isValidElement(children) && (children.props as { id?: string }).id === undefined
      ? cloneElement(children as ReactElement<{ id?: string }>, { id: controlId })
      : children;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={controlId} required={required === true}>
        {label}
      </Label>
      {control}
      {hint !== undefined && error === undefined ? (
        <p className="text-xs text-neutral-500">{hint}</p>
      ) : null}
      {error !== undefined ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
